import { useState, useEffect, useRef } from 'react';
import { Search, Loader2, TrendingUp, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/trackEvent';
import { retryQuery } from '@/lib/retryQuery';


interface CoinSearchResult {
  id: string;
  symbol: string;
  name: string;
  thumb: string;
  market_cap_rank: number | null;
}

interface TokenSearchProps {
  onSelect: (coinId: string) => void;
  isLoading?: boolean;
}

interface PopularCoin {
  id: string;
  symbol: string;
}

const POPULAR_LIMIT = 8;
const EXAMPLE_LIMIT = 3;
// In-scope universe: analyzed tokens on AU exchanges, $10M-$500M market cap.
const MIN_MARKET_CAP = 10_000_000;
const MAX_MARKET_CAP = 500_000_000;

export function TokenSearch({ onSelect, isLoading }: TokenSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CoinSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [popularCoins, setPopularCoins] = useState<PopularCoin[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await retryQuery(
        () =>
          supabase
            .from('token_opportunities')
            .select('token_symbol, token_id, opportunity_score, market_cap, collected_at')
            .gte('collected_at', cutoff)
            .gte('market_cap', MIN_MARKET_CAP)
            .lte('market_cap', MAX_MARKET_CAP)
            .order('opportunity_score', { ascending: false })
            .limit(40),
        { label: 'token_opportunities:popular' },
      );
      if (cancelled || error || !data) return;
      const seen = new Set<string>();
      const coins: PopularCoin[] = [];
      for (const row of data) {
        const sym = (row.token_symbol || '').toUpperCase();
        const id = row.token_id || sym.toLowerCase();
        if (!sym || seen.has(sym)) continue;
        seen.add(sym);
        coins.push({ id, symbol: sym });
        if (coins.length >= POPULAR_LIMIT + EXAMPLE_LIMIT) break;
      }
      setPopularCoins(coins);
    })();
    return () => { cancelled = true; };
  }, []);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchCoins = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetch(
          `https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(query)}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setResults(data.coins?.slice(0, 10) || []);
          setShowDropdown(true);
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchCoins, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const exampleCoins = popularCoins.slice(0, EXAMPLE_LIMIT);
  const restCoins = popularCoins.slice(EXAMPLE_LIMIT);

  const handleSelect = (coinId: string, entry: 'search' | 'quick_pick' = 'search') => {
    setShowDropdown(false);
    setQuery('');
    trackEvent('form_submit', { form_name: 'coin_review', coin: coinId, entry });
    trackEvent('coin_review_started', { coin: coinId, entry });
    onSelect(coinId);
  };


  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Enter coin name or contract address (e.g. BTC, ETH, or 0x…)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && setShowDropdown(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim().length >= 2) {
                handleSelect(query.trim(), 'search');
              }
            }}
            className="pl-12 pr-4 py-6 text-lg bg-card border-2 border-border focus:border-primary rounded-xl"
            disabled={isLoading}
          />
          {(isSearching || isLoading) && (
            <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-primary" />
          )}
        </div>

        {/* Search Results Dropdown */}
        {showDropdown && results.length > 0 && (
          <Card className="absolute z-50 w-full mt-2 max-h-80 overflow-y-auto bg-card border-2 border-border shadow-xl">
            {results.map((coin) => (
              <button
                key={coin.id}
                onClick={() => handleSelect(coin.id)}
                className="w-full flex items-center gap-3 p-3 hover:bg-secondary/50 transition-colors text-left"
              >
                <img
                  src={coin.thumb}
                  alt={coin.name}
                  className="w-8 h-8 rounded-full"
                />
                <div className="flex-1">
                  <span className="font-semibold text-foreground">{coin.name}</span>
                  <span className="text-muted-foreground ml-2">{coin.symbol.toUpperCase()}</span>
                </div>
                {coin.market_cap_rank && (
                  <span className="text-xs text-muted-foreground">
                    #{coin.market_cap_rank}
                  </span>
                )}
              </button>
            ))}
          </Card>
        )}
      </div>

      <Button
        size="lg"
        disabled={isLoading || query.trim().length < 2}
        onClick={() => handleSelect(query.trim(), 'search')}
        className="mt-4 w-full h-14 text-lg font-bold gap-2 bg-gradient-to-r from-primary to-purple-500"
      >
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
        Generate Review
      </Button>

      {/* Example coins — live, in-scope tokens from the same TOSS list */}
      {exampleCoins.length > 0 && (
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <span className="text-sm text-muted-foreground">Try an example:</span>
        {exampleCoins.map((coin) => (
          <Button
            key={coin.id}
            variant="secondary"
            size="sm"
            disabled={isLoading}
            onClick={() => handleSelect(coin.id, 'quick_pick')}
            className="rounded-full"
          >
            {coin.symbol}
          </Button>
        ))}
      </div>
      )}

      <p className="mt-4 text-sm text-muted-foreground">
        Analyzed tokens only — $10M–$500M on Australian exchanges.
      </p>

      {/* Popular Coins — live top TOSS picks */}
      {restCoins.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-muted-foreground">
              Top TOSS Picks Today
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {restCoins.map((coin) => (
              <Button
                key={coin.id}
                variant="outline"
                size="sm"
                onClick={() => handleSelect(coin.id, 'quick_pick')}

                disabled={isLoading}
                className="rounded-full hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                {coin.symbol}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
